import React, {useContext, useEffect, useState} from 'react'
import PropTypes from 'prop-types'

import {
    Button,
    Heading,
    Box,
    Text,
    Flex,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import useMultiSite from '@app/hooks/use-multi-site'
import colors from '@app/theme/foundations/colors.js'
import FieldsetForm from '@app/components/shared/ui/FieldsetForm/index.jsx'
import {IPContext} from '@app/custom-contexts'
import {useHistory} from 'react-router-dom'
import {useXSSProtection} from '@app/hooks/use-xss-protection.js'
import Field from '../../../components/field'
import useOutletCodeFields from '../../../components/forms/useOutletCodeFields'
import {BrandLogo} from '../../../components/custom-icons'
import {
    HOME_HREF,
    OUTLET_DISPATCH_URL,
    OUTLET_SESSION_PARAM,
    UNLIMITED_OUTLET_SESSION
} from '../../../constants'
import {useOutletConfigurations} from '../../../utils/outlet-utils'
import {setLocalStorageJSONItem} from '../../../utils/utils'

/**
 * OutletCode is a React functional component that renders the user interface and logic for entering and validating an outlet access code.
 *
 * This component is typically used as part of an e-commerce outlet entrance flow, where users must provide a valid code to access exclusive online outlet content. It manages form state, handles code validation, and provides feedback to the user. If the code is correct, it updates the session and redirects the user to the home page; if incorrect, it displays an error message. The component also offers an option to request a code if the feature is enabled.
 *
 * Key Features:
 * - Fetches and pre-fills the outlet code if available.
 * - Validates the entered code against the campaign code and checks for XSS vulnerabilities.
 * - Handles IP blocking by redirecting blocked users.
 * - Provides UI feedback for both successful and failed code submissions.
 * - Offers a "Get my code" button if the configuration allows.
 *
 * Props:
 * @param {Function} setIsGetCode - Callback to trigger the "Get my code" flow.
 * @param {boolean} isPrefilled - Determines if the code field should be prefilled with the campaign code.
 *
 * @returns {JSX.Element} The rendered outlet code entry form and associated UI elements.
 */
export const OutletCode = ({setIsGetCode, isPrefilled}) => {
    const {buildUrl} = useMultiSite()
    const {formatMessage} = useIntl()
    const [isGetCodeShown, setIsGetCodeShown] = useState(false)

    const [campaignCode, setCampaignCode] = useState('')
    const getOutletConfigurations = useOutletConfigurations()
    useEffect(() => {
        const fetchCampaignCode = async (isPrefilled) => {
            const configurations = await getOutletConfigurations(isPrefilled)
            if (configurations?.outletAccessCode) {
                if (configurations?.outletAccessCode) {
                    // Decode the Base64 encoded outletAccessCode
                    const decodedCode = atob(configurations.outletAccessCode)
                    setCampaignCode(decodedCode)
                }
            }
            setIsGetCodeShown(configurations?.outletGetCodeButtonActive)
        }

        fetchCampaignCode(isPrefilled)
    }, [isPrefilled])
    const form = useForm(
        isPrefilled && campaignCode
            ? {
                  defaultValues: {
                      code: campaignCode
                  }
              }
            : {}
    )
    const {isIPBlocked} = useContext(IPContext)
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()
    const fields = useOutletCodeFields({form})

    useEffect(() => {
        isPrefilled &&
            form.reset({
                code: campaignCode
            })
    }, [campaignCode])

    /**
     * Handles the submission and validation of the outlet code form.
     *
     * This asynchronous function processes the provided form data, performing several checks:
     * - If the user's IP is blocked, it redirects to the homepage and aborts further processing.
     * - If the code field is empty, the function exits early.
     * - Validates the form data for potential XSS attacks and exits if detected.
     * - Compares the submitted code with a reference code (`codeFromBM`). If they match, it updates the application context to set the outlet limitation to 'unlimited' and navigates to the home page.
     * - If the codes do not match, it sets a manual error on the form, displaying an appropriate error message to the user.
     *
     * @async
     * @function handleOutletCode
     * @param {Object} data - The form data containing the outlet code.
     * @param {string} data.code - The outlet code entered by the user.
     * @returns {Promise<void>} Resolves when the function completes its operations.
     */
    const handleOutletCode = async (data) => {
        if (isIPBlocked) {
            const path = buildUrl(OUTLET_DISPATCH_URL)
            history.push(path)
            return
        }

        let code = data.code

        if (!code) {
            return
        }

        const isXssDetected = validateXssForm(data)
        if (isXssDetected) {
            return
        }
        let decodedCode
        if (!campaignCode) {
            const configurations = await getOutletConfigurations(true)
            if (configurations?.outletAccessCode) {
                // Decode the Base64 encoded outletAccessCode
                decodedCode = atob(configurations.outletAccessCode)
                setCampaignCode(decodedCode)
            }
        }

        const validationCode = campaignCode || decodedCode
        // check the code, case insensitive manner
        if (validationCode && code.toLowerCase() === validationCode.toLowerCase()) {
            // Set outlet limitation to 'unlimited'
            setLocalStorageJSONItem(OUTLET_SESSION_PARAM, UNLIMITED_OUTLET_SESSION)
            // Redirect to home
            window.location.href = buildUrl(HOME_HREF)
        } else {
            form.setError('code', {
                type: 'manual',
                message: formatMessage({
                    defaultMessage: 'The code you entered is incorrect',
                    id: 'outlet_code.form.code.incorrect.error'
                })
            })
        }
    }

    return (
        <Flex
            maxWidth={'1100px'}
            padding={{base: '24px', lg: '24px 102px'}}
            margin={'auto'}
            backgroundColor={'white'}
            border={`1px solid ${colors.borderGray}`}
            justifyContent={'center'}
            flex={'1 0 100%'}
        >
            <Box maxWidth={'442px'} flex={1}>
                <Box textAlign={'center'}>
                    <BrandLogo width="94px" height="auto" mb={32} />
                    <Heading as={'h1'} size={'4xl'} mb={'16px'} textTransform={'uppercase'}>
                        <FormattedMessage
                            defaultMessage="Our e-outlet is open"
                            id="outlet_code.form.title"
                        />
                    </Heading>
                    <Text variant={'bodyBase2'} mb={'16px'}>
                        <FormattedMessage
                            defaultMessage="Enter your code and enjoy our exclusive online outlet."
                            id="outlet_code.form.text"
                        />
                    </Text>
                </Box>
                <FieldsetForm
                    id="outlet-code-form"
                    data-testid="sf-auth-modal-form"
                    legendText={formatMessage({
                        defaultMessage: 'Outlet Code form',
                        id: 'outlet_code.form.legend'
                    })}
                    onSubmit={form.handleSubmit(handleOutletCode)}
                    error={form.formState.errors?.global?.message}
                    spacing={16}
                >
                    <Field {...fields.code} />

                    <Button
                        type="submit"
                        onClick={() => {
                            form.clearErrors('global')
                        }}
                        isLoading={form.formState.isSubmitting}
                        variant="primary"
                    >
                        <FormattedMessage
                            defaultMessage="Enter"
                            id="outlet_code.form.button.label"
                        />
                    </Button>
                </FieldsetForm>
                {isGetCodeShown && (
                    <>
                        <Flex align="center" my={16}>
                            <Divider borderColor="borderGray" />
                            <Text variant="descriptor" textTransform={'uppercase'} mx={12}>
                                <FormattedMessage defaultMessage="or" id="outlet_code.or.label" />
                            </Text>
                            <Divider borderColor="borderGray" />
                        </Flex>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setIsGetCode(true)
                            }}
                            size="md"
                            w={'100%'}
                        >
                            <FormattedMessage
                                defaultMessage="Get my code"
                                id="outlet_code.get_code.button.label"
                            />
                        </Button>
                    </>
                )}
            </Box>
        </Flex>
    )
}

OutletCode.displayName = 'OutletCode'
OutletCode.propTypes = {
    setIsGetCode: PropTypes.func,
    isPrefilled: PropTypes.bool
}

export default OutletCode
